import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { Download, FileText, TrendingUp, DollarSign } from 'lucide-react';
import api from '../api';
import { Animal } from '../types';

const BREED_LABELS: Record<string, string> = {
  qorabosh: 'Qorabosh', golshtin: 'Golshtin', simmental: 'Simmental',
  abuerdeen: 'Aberdeen', mahalliy: 'Mahalliy',
};

export default function Reports({ onNavigate }: { onNavigate: (p: string, id?: number) => void }) {
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getAnimals().then(r => { setAnimals(r.data); setLoading(false); });
  }, []);

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  const active = animals.filter(a => a.status !== 'sotildi');
  const sick = animals.filter(a => ['kasal', 'davolanmoqda'].includes(a.status));

  // Feed efficiency comparison per animal (smart vs baseline)
  const efficiencyData = active
    .filter(a => a.latest_recommendation)
    .slice(0, 8)
    .map(a => {
      const rec = a.latest_recommendation!;
      const baselineFCR = 8.2; // Average traditional
      const saving_pct = Math.round(((baselineFCR - rec.fcr) / baselineFCR) * 100);
      return {
        name: a.tag_id,
        smart_fcr: rec.fcr,
        baseline_fcr: baselineFCR,
        saving: saving_pct,
      };
    });

  // Monthly feed cost vs weight gain (from animals' data)
  const gainVsCost = active.filter(a => a.daily_gain > 0).map(a => ({
    name: a.tag_id,
    gain: a.daily_gain,
    weight: a.latest_weight,
    breed: BREED_LABELS[a.breed] || a.breed,
  })).sort((a, b) => b.gain - a.gain).slice(0, 10);

  // Total feed investment stats
  const totalRecs = active.filter(a => a.latest_recommendation);
  const totalDailyFeed = totalRecs.reduce((s, a) => s + (a.latest_recommendation?.daily_feed_kg || 0), 0);
  const avgFCR = totalRecs.length
    ? (totalRecs.reduce((s, a) => s + (a.latest_recommendation?.fcr || 0), 0) / totalRecs.length)
    : 0;
  const sellReadyAnimals = active.filter(a => a.latest_recommendation?.sell_ready);
  const estimatedRevenue = sellReadyAnimals.reduce((s, a) => s + a.latest_weight * 58000, 0);

  // CSV export
  const exportCSV = () => {
    const headers = ['Tag ID', 'Zot', 'Holat', 'Hozirgi vazn', "Jami o'sish", 'ADG', 'Tavsiya yem', 'FCR', 'Sotish holati'];
    const rows = active.map(a => {
      const rec = a.latest_recommendation;
      return [
        a.tag_id, BREED_LABELS[a.breed] || a.breed, a.status,
        a.latest_weight, a.total_gain, a.daily_gain,
        rec?.daily_feed_kg || '', rec?.fcr || '',
        rec?.sell_ready ? 'Tayyor' : `${rec?.days_remaining || '?'} kun`,
      ].join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `chorvalar_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="page-body">
      {/* Summary cards */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card teal">
          <div className="stat-label">Jami kunlik yem</div>
          <div className="stat-value">{totalDailyFeed.toFixed(0)} <span style={{ fontSize: 14 }}>kg</span></div>
          <div className="stat-sub">{active.length} ta faol mol uchun</div>
        </div>
        <div className="stat-card amber">
          <div className="stat-label">O'rtacha FCR</div>
          <div className="stat-value">{avgFCR.toFixed(2)}</div>
          <div className="stat-sub">Ozuqa samaradorligi</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Sotish daromadi (taxmin)</div>
          <div className="stat-value" style={{ fontSize: 18 }}>{(estimatedRevenue / 1_000_000).toFixed(1)} <span style={{ fontSize: 13 }}>mln so'm</span></div>
          <div className="stat-sub">{sellReadyAnimals.length} ta tayyor mol</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid var(--red)' }}>
          <div className="stat-label">Kasal/Davolanmoqda</div>
          <div className="stat-value" style={{ color: 'var(--red)' }}>{sick.length}</div>
          <div className="stat-sub">ta chorva nazoratda</div>
        </div>
      </div>

      {/* Export bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <button className="btn btn-primary" onClick={exportCSV}>
          <Download size={15} /> CSV yuklab olish
        </button>
        <button className="btn btn-outline" onClick={() => window.print()}>
          <FileText size={15} /> Chop etish
        </button>
      </div>

      {/* Charts row 1 */}
      <div className="charts-grid">
        <div className="card">
          <div className="card-header">
            <h3>FCR taqqoslamasi: Smart vs An'anaviy</h3>
            <span style={{ fontSize: 12, color: 'var(--gray)' }}>Past = yaxshiroq</span>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={efficiencyData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} domain={[0, 10]} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={70} />
                <Tooltip formatter={(v: any, name: any) => [v, name === 'smart_fcr' ? 'Smart tizim' : "An'anaviy"]} />
                <Legend formatter={(v) => v === 'smart_fcr' ? 'Smart tizim' : "An'anaviy usul"} />
                <Bar dataKey="baseline_fcr" fill="#D1D5DB" radius={[0, 4, 4, 0]} />
                <Bar dataKey="smart_fcr" fill="#0D7E6E" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Kunlik o'sish reytingi</h3>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={gainVsCost} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} unit=" kg" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={70} />
                <Tooltip formatter={(v: any) => [`${v} kg/kun`, 'ADG']} />
                <Bar dataKey="gain" radius={[0, 4, 4, 0]}>
                  {gainVsCost.map((entry, i) => (
                    <rect key={i} fill={entry.gain >= 1.2 ? '#0D7E6E' : entry.gain >= 1.0 ? '#14A88A' : '#E5831A'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Full animal report table */}
      <div className="card">
        <div className="card-header">
          <h3>To'liq hisobot — barcha faol chorvalar</h3>
          <button className="btn btn-outline btn-sm" onClick={exportCSV}><Download size={13} /> CSV</button>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Tag ID</th><th>Zot</th><th>Holat</th>
                <th>Vazn</th><th>O'sish</th><th>ADG</th>
                <th>Tavsiya yem</th><th>FCR</th><th>Maqsad</th><th>Sotish</th>
              </tr>
            </thead>
            <tbody>
              {active.map(a => {
                const rec = a.latest_recommendation;
                return (
                  <tr key={a.id} onClick={() => onNavigate('animal-detail', a.id)} style={{ cursor: 'pointer' }}>
                    <td><span className="tag-id">{a.tag_id}</span></td>
                    <td style={{ fontSize: 12 }}>{BREED_LABELS[a.breed] || a.breed}</td>
                    <td><span className={`badge badge-${a.status}`}>{a.status}</span></td>
                    <td><strong>{a.latest_weight} kg</strong></td>
                    <td style={{ color: 'var(--teal)', fontWeight: 600 }}>+{a.total_gain} kg</td>
                    <td style={{ fontSize: 13 }}>{a.daily_gain > 0 ? `${a.daily_gain}` : '—'}</td>
                    <td style={{ color: 'var(--teal)', fontWeight: 600 }}>
                      {rec ? `${rec.daily_feed_kg} kg` : '—'}
                    </td>
                    <td>
                      {rec ? (
                        <span style={{
                          color: rec.fcr < 6.5 ? 'var(--green)' : rec.fcr < 7.5 ? 'var(--amber)' : 'var(--red)',
                          fontWeight: 600, fontSize: 13
                        }}>{rec.fcr}</span>
                      ) : '—'}
                    </td>
                    <td style={{ fontSize: 12 }}>{rec ? `${rec.target_weight} kg` : '—'}</td>
                    <td>
                      {rec?.sell_ready
                        ? <span className="badge badge-sell">✓ Tayyor</span>
                        : rec
                          ? <span style={{ fontSize: 12, color: 'var(--gray)' }}>{rec.days_remaining} kun</span>
                          : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
